import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ChevronRight,
  ListTree,
  CheckCircle2,
  Clock,
  Filter,
  Loader2,
  MinusCircle,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { ApiError, listRuns, listTypes } from '../api/client.js';
import RunDetailModal from '../components/RunDetailModal.jsx';

/**
 * Console unifiée des batchs.
 *
 * Le principe de cette vue : elle ne connaît AUCUN batch en particulier. Elle
 * n'affiche que le contrat commun rendu par `BatchRun` (type, app, status,
 * totals, rejects). Ajouter un batch — congés, propositions commerciales,
 * affaires — ne demande donc AUCUNE modification ici : il apparaît dès qu'il
 * publie son premier run. C'est toute la différence avec un écran par batch.
 */

/** Présentation d'un statut : couleur, icône, libellé. Vocabulaire commun. */
const STATUS = {
  SUCCESS: { label: 'Succès', color: 'var(--success)', Icon: CheckCircle2 },
  PARTIAL: { label: 'Partiel', color: 'var(--warning)', Icon: AlertTriangle },
  FAILED: { label: 'Échec', color: 'var(--danger)', Icon: XCircle },
  SKIPPED: { label: 'Ignoré', color: 'var(--text-muted)', Icon: MinusCircle },
  DRY_RUN: { label: 'Simulation', color: 'var(--primary)', Icon: Filter },
  RUNNING: { label: 'En cours', color: 'var(--primary)', Icon: Loader2 },
};

const FILTER_STATUSES = ['SUCCESS', 'PARTIAL', 'FAILED', 'SKIPPED', 'DRY_RUN', 'RUNNING'];

/** Durée lisible : on reste en ms sous la seconde, sinon secondes/minutes. */
function formatDuration(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60_000)} min ${Math.round((ms % 60_000) / 1000)} s`;
}

/** Ancienneté relative — c'est le signal de fraîcheur le plus lisible. */
function formatAge(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return '—';
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  return `il y a ${Math.floor(h / 24)} j`;
}

function StatusPill({ status }) {
  const s = STATUS[status] ?? { label: status, color: 'var(--text-muted)', Icon: Clock };
  const { Icon } = s;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 10px',
        borderRadius: 'var(--radius-pill)',
        border: `1px solid ${s.color}`,
        color: s.color,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      <Icon size={13} />
      {s.label}
    </span>
  );
}

/** Pills de filtre — jamais de checkbox ni de radio (convention UI Synotis). */
function FilterPills({ options, value, onChange, allLabel = 'Tous' }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {[{ key: '', label: allLabel }, ...options].map((o) => {
        const active = value === o.key;
        return (
          <button
            key={o.key || '__all'}
            type="button"
            onClick={() => onChange(o.key)}
            style={{
              padding: '4px 12px',
              borderRadius: 'var(--radius-pill)',
              border: `1px solid ${active ? 'var(--brand)' : 'var(--glass-border)'}`,
              background: active ? 'var(--brand)' : 'transparent',
              color: active ? '#fff' : 'var(--text-muted)',
              fontSize: 12,
              fontWeight: active ? 600 : 500,
              cursor: 'pointer',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Tuile de synthèse par typologie : l'essentiel se lit sans ouvrir la liste. */
function TypeCard({ type, onSelect, selected }) {
  const s = STATUS[type.lastStatus] ?? {};
  const stale = type.lastSuccessAt
    ? Date.now() - new Date(type.lastSuccessAt).getTime() > 26 * 3600 * 1000
    : true;
  return (
    <button
      type="button"
      onClick={() => onSelect(selected ? '' : type.type)}
      style={{
        textAlign: 'left',
        padding: 14,
        borderRadius: 'var(--radius-md)',
        background: 'var(--card-bg)',
        border: `1px solid ${selected ? 'var(--brand)' : 'var(--glass-border)'}`,
        boxShadow: 'var(--shadow-card)',
        cursor: 'pointer',
        minWidth: 220,
        flex: '1 1 220px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'start' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{type.label || type.type}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            {type.type} · {type.app}
          </div>
        </div>
        <StatusPill status={type.lastStatus} />
      </div>
      <div style={{ marginTop: 10, display: 'flex', gap: 16, fontSize: 12 }}>
        <span style={{ color: stale ? 'var(--danger)' : 'var(--text-muted)' }}>
          Dernier succès {formatAge(type.lastSuccessAt)}
        </span>
        {type.failures7d > 0 && (
          <span style={{ color: 'var(--warning)' }}>{type.failures7d} échec(s) / 7 j</span>
        )}
      </div>
    </button>
  );
}

export default function BatchRunsView() {
  const [types, setTypes] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [openRunId, setOpenRunId] = useState(null);

  const load = useCallback(
    // `silent` : un rafraîchissement de fond ne doit pas faire clignoter la
    // liste sur « Chargement… » toutes les dix secondes.
    async (signal, { silent = false } = {}) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        const [t, r] = await Promise.all([
          listTypes(signal).catch(() => ({ items: [] })),
          listRuns({ type: typeFilter, status: statusFilter, limit: 100 }, signal),
        ]);
        setTypes(t?.items ?? []);
        setRuns(r?.items ?? []);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        setError(err);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [typeFilter, statusFilter],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  // Un batch en cours dure plusieurs minutes : sans rafraîchissement, la liste
  // resterait figée sur « En cours » jusqu'à ce qu'on recharge la page à la
  // main. On sonde tant qu'une exécution est ouverte, et pas une seconde de
  // plus — l'historique, lui, ne bouge pas tout seul.
  const hasRunning = runs.some((r) => r.status === 'RUNNING');
  useEffect(() => {
    if (!hasRunning) return undefined;
    const ctrl = new AbortController();
    const timer = setInterval(() => load(ctrl.signal, { silent: true }), 10_000);
    return () => {
      clearInterval(timer);
      ctrl.abort();
    };
  }, [hasRunning, load]);

  const typeOptions = useMemo(
    () => types.map((t) => ({ key: t.type, label: t.label || t.type })),
    [types],
  );

  return (
    <div style={{ padding: '0 4px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginBottom: 14,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18 }}>Exécutions</h2>
        <button
          type="button"
          onClick={() => load()}
          title="Rafraîchir"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--glass-border)',
            background: 'var(--glass)',
            color: 'var(--text)',
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          <RefreshCw size={14} /> Rafraîchir
        </button>
      </div>

      {types.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
          {types.map((t) => (
            <TypeCard
              key={t.type}
              type={t}
              selected={typeFilter === t.type}
              onSelect={setTypeFilter}
            />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {typeOptions.length > 0 && (
          <FilterPills
            options={typeOptions}
            value={typeFilter}
            onChange={setTypeFilter}
            allLabel="Tous les batchs"
          />
        )}
        <FilterPills
          options={FILTER_STATUSES.map((s) => ({ key: s, label: STATUS[s].label }))}
          value={statusFilter}
          onChange={setStatusFilter}
          allLabel="Tous les statuts"
        />
      </div>

      {error && <ErrorPanel error={error} onRetry={() => load()} />}

      {loading && !error && (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
          <Loader2 size={18} style={{ verticalAlign: -3 }} /> Chargement…
        </div>
      )}

      {!loading && !error && runs.length === 0 && (
        <div
          style={{
            padding: 32,
            textAlign: 'center',
            color: 'var(--text-muted)',
            background: 'var(--card-bg)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--glass-border)',
          }}
        >
          Aucune exécution ne correspond à ces filtres.
        </div>
      )}

      {!loading && !error && runs.length > 0 && (
        <>
          <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-muted)' }}>
            Cliquez sur une exécution pour voir son traitement étape par étape, les entités
            traitées et les rejets.
          </p>
          <RunsTable runs={runs} onOpen={setOpenRunId} />
        </>
      )}

      {openRunId && <RunDetailModal runId={openRunId} onClose={() => setOpenRunId(null)} />}
    </div>
  );
}

function RunsTable({ runs, onOpen }) {
  return (
    <div
      style={{
        overflowX: 'auto',
        background: 'var(--card-bg)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--glass-border)',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 860 }}>
        <thead>
          <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
            {[
              'Date',
              'Batch',
              'App',
              'Statut',
              'Référence',
              'Lu',
              'Créés',
              'Maj',
              'Rejets',
              'Durée',
              'Détail',
            ].map(
              (h) => (
                <th key={h} style={{ padding: '10px 12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => {
            const t = r.totals ?? {};
            return (
              <tr
                key={r.id}
                onClick={() => onOpen(r.id)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--glass)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
                title="Ouvrir le détail : traitement étape par étape, entités traitées, rejets"
                style={{
                  borderTop: '1px solid var(--glass-border)',
                  cursor: 'pointer',
                  transition: 'background .12s',
                }}
              >
                <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                  <div>{new Date(r.startedAt).toLocaleString('fr-CH')}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {formatAge(r.startedAt)}
                  </div>
                </td>
                <td style={{ padding: '9px 12px' }}>
                  {r.label || r.type}
                  {r.dryRun && (
                    <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--primary)' }}>
                      simulation
                    </span>
                  )}
                </td>
                <td style={{ padding: '9px 12px', color: 'var(--text-muted)' }}>{r.app}</td>
                <td style={{ padding: '9px 12px' }}>
                  <StatusPill status={r.status} />
                </td>
                <td
                  style={{
                    padding: '9px 12px',
                    maxWidth: 220,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: 'var(--text-muted)',
                  }}
                  title={r.reference || ''}
                >
                  {r.reference || '—'}
                </td>
                <td style={{ padding: '9px 12px' }}>{t.read ?? '—'}</td>
                <td style={{ padding: '9px 12px' }}>{t.created ?? '—'}</td>
                <td style={{ padding: '9px 12px' }}>{t.updated ?? '—'}</td>
                <td
                  style={{
                    padding: '9px 12px',
                    color: t.rejected > 0 ? 'var(--danger)' : 'inherit',
                    fontWeight: t.rejected > 0 ? 700 : 400,
                  }}
                >
                  {t.rejected ?? '—'}
                </td>
                <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                  {formatDuration(r.durationMs)}
                </td>
                <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      color: 'var(--brand)',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <ListTree size={13} /> Voir
                    <ChevronRight size={13} />
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Message d'erreur actionnable. Le 401 est le cas le plus fréquent au premier
 * accès (session SSO absente) et mérite une consigne, pas un code d'erreur.
 */
function ErrorPanel({ error, onRetry }) {
  const isAuth = error instanceof ApiError && (error.status === 401 || error.status === 403);
  return (
    <div
      style={{
        padding: 18,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--danger)',
        background: 'var(--glass)',
        marginBottom: 14,
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--danger)' }}>
        <AlertTriangle size={16} />
        <strong>{isAuth ? 'Accès refusé' : 'Impossible de charger les exécutions'}</strong>
      </div>
      <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
        {isAuth
          ? "Session absente ou rôle insuffisant. Connectez-vous à l'écosystème Synotis, puis rechargez cette page. La console est réservée aux administrateurs."
          : error.message}
      </p>
      <button
        type="button"
        onClick={onRetry}
        style={{
          marginTop: 10,
          padding: '6px 12px',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--glass-border)',
          background: 'var(--glass)',
          color: 'var(--text)',
          cursor: 'pointer',
          fontSize: 13,
        }}
      >
        Réessayer
      </button>
    </div>
  );
}
